import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import sapEndToEndImage from "@/assets/sap-end-to-end-solutions-clean.png";

const SAPEndToEndDetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutions = [
    {
      title: "SAP IBP (Integrated Business Planning)",
      description: "Comprehensive demand and supply planning solution for optimized business operations",
      modules: [
        "Demand Planning - Forecast accuracy and demand sensing",
        "Supply Planning - Optimal inventory and supply chain balance", 
        "Inventory Optimization - Right inventory at the right place",
        "S&OP - Sales and Operations Planning integration",
        "Response & Supply - Rapid supply chain response",
        "What-if Simulations - Scenario planning and analysis"
      ],
      benefits: [
        "Real-time demand and supply visibility",
        "Reduced inventory costs by 15-30%",
        "Improved forecast accuracy by 20-40%",
        "Enhanced supply chain agility",
        "Better collaboration across functions"
      ]
    },
    {
      title: "SAP S/4HANA",
      description: "Next-generation ERP suite with real-time processing and intelligent technologies",
      modules: [
        "SD - Sales & Distribution for order-to-cash",
        "MM - Materials Management for procurement",
        "PP - Production Planning for manufacturing",
        "WM/EWM - Warehouse Management for logistics",
        "FI/CO - Finance and Controlling for financial operations"
      ],
      benefits: [
        "Real-time business insights with in-memory computing",
        "Simplified data model for faster processing",
        "Embedded AI and machine learning",
        "Cloud-ready and mobile-enabled",
        "Reduced TCO by 30-50%"
      ]
    },
    {
      title: "SAP PP/DS (Production Planning/Detailed Scheduling)",
      description: "Advanced planning and scheduling for manufacturing excellence",
      modules: [
        "Order Sequencing - Optimal production order sequence",
        "Bottleneck Optimization - Identify and resolve constraints",
        "Finite Capacity Scheduling - Resource-constrained planning",
        "Gantt Chart Visualization - Visual production planning",
        "Constraint-based Planning - Rule-based optimization",
        "Production Confirmations - Real-time shop floor data"
      ],
      benefits: [
        "Increased production efficiency by 20-35%",
        "Reduced lead times by 15-25%",
        "Improved on-time delivery to 95%+",
        "Better resource utilization",
        "Real-time visibility into production"
      ]
    },
    {
      title: "SAP DMC (Digital Manufacturing Cloud)",
      description: "Cloud-based manufacturing execution and operations management",
      modules: [
        "MES - Manufacturing Execution System",
        "IoT - Internet of Things integration",
        "Quality - Quality management and control",
        "OEE - Overall Equipment Effectiveness",
        "Work Instructions - Digital work procedures",
        "Operator Dashboards - Real-time production visibility"
      ],
      benefits: [
        "Paperless shop floor operations",
        "Real-time production tracking",
        "Improved quality with digital workflows",
        "Enhanced OEE by 10-20%",
        "Faster time-to-value with cloud deployment"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="pt-20 pb-16">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-background py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button 
              variant="ghost" 
              onClick={() => window.history.back()}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            
            <div className="space-y-4">
              <Badge className="bg-orange-600 text-white hover:bg-orange-700">
                SAP End-to-End Solutions
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Integrated Manufacturing & Supply Chain Solutions
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl">
                Complete end-to-end solutions connecting planning, execution, and manufacturing operations for seamless business processes
              </p>
            </div>
          </div>
        </div>

        {/* Solution Architecture Diagram */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle className="text-2xl">End-to-End Solution Architecture</CardTitle>
              <p className="text-muted-foreground mt-2">
                Integrated flow from planning to manufacturing execution
              </p>
            </CardHeader>
            <CardContent>
              <div className="bg-black/90 rounded-lg p-4 md:p-8">
                <img 
                  src={sapEndToEndImage} 
                  alt="SAP End-to-End Solutions Architecture showing IBP, S/4HANA, PP/DS, and DMC integration"
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold text-blue-600 dark:text-blue-400">SAP IBP</h4>
                  <p className="text-sm text-muted-foreground mt-1">Demand & Supply Planning</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold text-green-600 dark:text-green-400">SAP S/4HANA</h4>
                  <p className="text-sm text-muted-foreground mt-1">Core ERP Operations</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold text-yellow-600 dark:text-yellow-400">SAP PP/DS</h4>
                  <p className="text-sm text-muted-foreground mt-1">Production Planning</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold text-purple-600 dark:text-purple-400">SAP DMC</h4>
                  <p className="text-sm text-muted-foreground mt-1">Manufacturing Execution</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Solutions Detail */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="space-y-8">
            {solutions.map((solution, idx) => (
              <Card key={idx} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-primary/5 to-background">
                  <CardTitle className="text-2xl">{solution.title}</CardTitle>
                  <p className="text-muted-foreground mt-2">{solution.description}</p>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Modules */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <CheckCircle className="h-5 w-5 text-orange-600 mr-2" />
                        Key Modules & Capabilities
                      </h3>
                      <ul className="space-y-3">
                        {solution.modules.map((module, mIdx) => (
                          <li key={mIdx} className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-orange-600 mt-2 mr-3 flex-shrink-0" />
                            <span className="text-muted-foreground">{module}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Benefits */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        Business Benefits
                      </h3>
                      <ul className="space-y-3">
                        {solution.benefits.map((benefit, bIdx) => (
                          <li key={bIdx} className="flex items-start">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-600 mt-2 mr-3 flex-shrink-0" />
                            <span className="text-muted-foreground">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default SAPEndToEndDetail;